import { useState } from 'react';
import { Megaphone } from 'lucide-react';
import { announcementsApi } from '../api/announcements';
import type { AttachmentInput } from '../api/announcements';
import { ATTACHMENT_LIMIT } from '../lib/attachments';
import { ComplianceRefusal } from '../api/conversations';
import { ValidationFailure } from '../api/events';
import type { FieldErrors } from '../api/events';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { CuratedText } from '../ui/CuratedText';
import { SelectField, TextAreaField, TextField } from '../ui/Field';
import { AttachmentDraftsEditor, useAttachmentDrafts } from '../ui/AttachmentDrafts';

/**
 * Publishing standing information.
 *
 * The form is deliberately short. An announcement is a headline and a paragraph that a family
 * reads at a glance; anything that needs a form with ten boxes is an event, and the product has
 * one of those.
 */
export function NewAnnouncementScreen({
  unitId,
  unitName,
  allowsYouthParticipants,
  onPublished,
  onCancel,
}: {
  unitId: string;
  unitName: string;
  /**
   * False in a Pack, whose youth are never conversation participants. There is then only one
   * audience an announcement's thread can have, so the picker is replaced by a line saying what
   * it will be — see `NewEventScreen`, which makes the same call for the same reason.
   */
  allowsYouthParticipants: boolean;
  onPublished: (announcementId: string) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  // Seeded from the unit, exactly as the new-event form does it. A constant here is what stopped
  // a Pack publishing at all (TROOP-37).
  const [audience, setAudience] = useState<'Everyone' | 'AdultsOnly'>(
    allowsYouthParticipants ? 'Everyone' : 'AdultsOnly',
  );

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // What the announcement will hand out, in the order it will show them. One list, drawn and sent
  // -- the editor is shared with the Info page's sections (TROOP-256), so the two ask for a link
  // the same way.
  const drafts = useAttachmentDrafts((file) => announcementsApi.uploadDocument(unitId, file));
  const uploading = drafts.uploading;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (uploading) {
      return;
    }
    setBusy(true);
    setFieldErrors({});
    setError(null);

    try {
      const published = await announcementsApi.publish(unitId, {
        title: title.trim(),
        body: body.trim(),
        isPinned,
        threadAudience: audience,
        // Composed from the rows on screen, in the order they are drawn -- never from a second
        // list kept beside them. The submit button refuses while an upload is in flight, so every
        // document row here has its id.
        // Nothing is kept on a new announcement, so every row is a new document or link.
        attachments: drafts.toInput().filter((row): row is AttachmentInput => !('id' in row)),
      });
      onPublished(published.announcementId);
    } catch (e) {
      if (e instanceof ValidationFailure) {
        setFieldErrors(e.errors);
      } else if (e instanceof ComplianceRefusal) {
        setError(`${e.title} ${e.message}`);
      } else {
        setError(e instanceof Error ? e.message : 'Could not publish it.');
      }
      setBusy(false);
    }
  }

  const incomplete = !title.trim()
    ? 'Give it a headline first.'
    : !body.trim()
      ? 'Say what it is.'
      : drafts.incomplete;

  return (
    <Page>
      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p
              className="t-eyebrow inline-flex items-center gap-2"
              style={{ color: 'var(--color-accent-announcement-text)', margin: 0 }}
            >
              <Megaphone size={14} aria-hidden="true" />
              {unitName}
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 0' }}
            >
              Post an announcement
            </h1>
            <p
              className="t-body"
              style={{
                color: 'var(--color-text-secondary)',
                margin: 'var(--space-2) 0 0',
                maxWidth: '46ch',
              }}
            >
              This sits at the top of the unit&rsquo;s page and stays there. Put the answer here,
              not in the thread underneath it.
            </p>
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <form onSubmit={submit} style={{ paddingBlock: 'var(--space-6)', maxWidth: '40rem' }}>
            <div className="flex flex-col" style={{ gap: 'var(--space-5)' }}>
              <TextField
                label="Headline"
                required
                value={title}
                maxLength={300}
                onChange={(e) => setTitle(e.currentTarget.value)}
                error={fieldErrors.title?.[0]}
                hint="What a family needs to know, in one line."
              />

              <TextAreaField
                label="What it says"
                required
                rows={6}
                value={body}
                onChange={(e) => setBody(e.currentTarget.value)}
                error={fieldErrors.body?.[0]}
                hint="The whole answer. If people have to ask in the thread, it belongs up here instead. **bold**, *italic*, a line starting with - for a list, and [words](https://...) for a link."
              />

              {/* What families will read, drawn by the card's own renderer (TROOP-255) -- so a link
                  shows here with the host beside it exactly as it will on the noticeboard, and one
                  the renderer will not draw shows as the characters typed before the server says
                  why. Only once there is something to show. */}
              {body.trim() && (
                <div className="flex flex-col" style={{ gap: 'var(--space-1)' }}>
                  <span className="t-label" style={{ color: 'var(--color-text-muted)' }}>
                    How it will read
                  </span>
                  <CuratedText
                    text={body}
                    style={{
                      color: 'var(--color-text-primary)',
                      background: 'var(--color-surface-raised)',
                      borderLeft: '5px solid var(--color-accent-announcement)',
                      borderRadius: '0 var(--radius-md) var(--radius-md) 0',
                      padding: 'var(--space-3) var(--space-4)',
                    }}
                  />
                </div>
              )}

              <AttachmentDraftsEditor
                drafts={drafts}
                limit={ATTACHMENT_LIMIT}
                atLimitMessage="That is as many as one announcement can carry. Remove one to add another."
                error={fieldErrors.attachments?.[0]}
              >
                <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                  Up to {ATTACHMENT_LIMIT} &mdash; documents or links. A document is a PDF or a
                  picture. Each one shows under the announcement, and a link shows where it
                  goes.
                </p>
                {/* The settled nudge: a handout is not a returned form. No control here --
                    this form does not know which event it would be. */}
                <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                  This is for handouts and things to read. If every family has to hand something
                  back, like a signed form, add it to the event&rsquo;s Forms instead &mdash;
                  that is where you can see who has done it.
                </p>
              </AttachmentDraftsEditor>

              {allowsYouthParticipants ? (
                <SelectField
                  label="Who it is for"
                  required
                  value={audience}
                  onChange={(e) => setAudience(e.currentTarget.value as 'Everyone' | 'AdultsOnly')}
                  error={fieldErrors.threadAudience?.[0]}
                  hint="Adults only keeps the thread to leaders and parents. The two-adult rule does not apply to a thread with no youth in it."
                >
                  <option value="Everyone">Everyone in the unit</option>
                  <option value="AdultsOnly">Adults only</option>
                </SelectField>
              ) : (
                <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
                  This reaches {unitName}&rsquo;s leaders and every parent. Its youth are not in
                  the unit&rsquo;s conversations, so there is no narrower audience to choose.
                </p>
              )}

              <label className="flex items-start gap-3" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isPinned}
                  onChange={(e) => setIsPinned(e.currentTarget.checked)}
                  style={{ marginTop: '0.25em', width: 18, height: 18, flexShrink: 0 }}
                />
                <span>
                  <span className="t-body-strong" style={{ color: 'var(--color-text-primary)' }}>
                    Pin it to the top
                  </span>
                  <span
                    className="t-caption"
                    style={{ display: 'block', color: 'var(--color-text-secondary)' }}
                  >
                    For something that stays true for a while — recharter, summer camp deadlines.
                  </span>
                </span>
              </label>

              {error && (
                <p
                  className="t-body"
                  role="alert"
                  style={{
                    background: 'var(--color-status-blocked-surface)',
                    border: '1px solid var(--color-status-blocked)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-3)',
                    color: 'var(--color-text-primary)',
                    margin: 0,
                  }}
                >
                  {error}
                </p>
              )}

              <div className="flex flex-wrap gap-3">
                <Button variant="primary" type="submit" disabledReason={incomplete}>
                  {busy ? 'Posting…' : 'Post it'}
                </Button>

                <Button variant="secondary" onClick={onCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}
